import { Location } from 'history';
import * as React from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { match, Redirect, Route, RouteComponentProps, Switch } from 'react-router';

import { PublishedConnectionTableSubspaceName } from '~src/app/state/SpaceInfo';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { AppState } from '../../app/state/AppState';
import { PublishedConnectionTableSpaceProvider } from './PublishedConnectionTableSpaceProvider';

export interface PublishedConnectionTableRouteParams {
  readonly id: string;
}

export interface PublishedConnectionTableRouteComponentProps extends RouteComponentProps<PublishedConnectionTableRouteParams> {
  readonly match: match<PublishedConnectionTableRouteParams>;
  readonly location: Location;
}

export interface PublishedConnectionTableSpaceRouterOwnProps {
  readonly siteName: string;
  readonly previousUrl: string;
  readonly urls: SpacesSiteUrls;
}

export interface PublishedConnectionTableSpaceRouterStateProps {
  readonly isRequestAccessEnabled: boolean;
  readonly catalogingOnSiteEnabled: boolean;
}

export type PublishedConnectionTableSpaceRouterProps =
  PublishedConnectionTableRouteComponentProps &
  PublishedConnectionTableSpaceRouterOwnProps &
  PublishedConnectionTableSpaceRouterStateProps;

export class PublishedConnectionTableSpaceRouterComponent extends React.Component<PublishedConnectionTableSpaceRouterProps, {}> {
  public render() {
    return (
      <Switch>
        <Route exact path={this.props.urls.publishedConnectionTablePath(':id')} render={(routeProps: PublishedConnectionTableRouteComponentProps) =>
          <Redirect to={this.props.urls.publishedConnectionTableOverviewPath(routeProps.match.params.id)} />
        } />
        <Route exact path={this.props.urls.publishedConnectionTableOverviewPath(':id')} render={this.spaceProviderFor('overview')} />
        <Route exact path={this.props.urls.publishedConnectionTableScheduledTasksPath(':id')} render={this.spaceProviderFor('scheduledTasks')} />
        {this.getLineageRoute()}
        <Route render={() => <RouterError errorType={'default'} urls={this.props.urls} />} />
      </Switch>
    );
  }

  private getLineageRoute = (): JSX.Element => {
    if (FeatureFlags.publishedConnectionsLineageEnabled && this.props.catalogingOnSiteEnabled) {
      return <Route path={this.props.urls.publishedConnectionTableLineagePath(':id')} render={this.spaceProviderFor('lineage')} />;
    } else {
      // we want to match on any virtual connection table path starting with /lineage so don't use 'exact' on the route
      return (
        <Route path={this.props.urls.publishedConnectionTableLineagePath(':id')} render={({ match }) => (
          <Redirect to={this.props.urls.publishedConnectionTableOverviewPath(match.params.id)} />
        )} />);
    }
  }

  private spaceProviderFor = (subplace: PublishedConnectionTableSubspaceName): (routeProps: PublishedConnectionTableRouteComponentProps) => JSX.Element => {
    return (routeProps: PublishedConnectionTableRouteComponentProps) => {
      return (
        <PublishedConnectionTableSpaceProvider
          siteName={this.props.siteName}
          publishedConnectionTableId={routeProps.match.params.id}
          siteUrls={this.props.urls}
          subplaceName={subplace}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
          isRequestAccessEnabled={this.props.isRequestAccessEnabled}
        />
      );
    };
  }
}

const mapStateToProps: MapStateToProps<PublishedConnectionTableSpaceRouterStateProps, PublishedConnectionTableSpaceRouterOwnProps, {}> = (state: AppState) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state),
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state)
});

export const PublishedConnectionTableSpaceRouter: React.ComponentClass<PublishedConnectionTableSpaceRouterOwnProps>
  = connect(mapStateToProps, null, null, { pure: false })(PublishedConnectionTableSpaceRouterComponent);
